import { useState, useEffect, useRef } from "react";
import { getPage } from "../firebase/database";

/**
 * Hook to fetch page content from Firebase
 * @param {string} pageId - The ID of the page to fetch
 * @param {object} defaultContent - Default content to use if Firebase data is not available
 * @param {string} defaultTitle - Default title to use if Firebase data is not available
 * @returns {object} { content, title, loading, error }
 */
const useFirebaseContent = (pageId, defaultContent = {}, defaultTitle = "") => {
  const [content, setContent] = useState(defaultContent);
  const [title, setTitle] = useState(defaultTitle);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Store the defaults in refs to avoid dependency issues
  const defaultContentRef = useRef(defaultContent);
  const defaultTitleRef = useRef(defaultTitle);

  // Update refs if defaults change
  useEffect(() => {
    defaultContentRef.current = defaultContent;
    defaultTitleRef.current = defaultTitle;
  }, [defaultContent, defaultTitle]);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try to get content from Firebase
        const pageData = await getPage(pageId);

        if (pageData) {
          // If Firebase content exists, use it
          if (pageData.content) {
            setContent(pageData.content);
          } else {
            setContent(defaultContentRef.current);
          }

          // Set title if available
          if (pageData.title) {
            setTitle(pageData.title);
          } else {
            setTitle(defaultTitleRef.current);
          }
        } else {
          // If no Firebase data, use defaults
          setContent(defaultContentRef.current);
          setTitle(defaultTitleRef.current);
        }
      } catch (err) {
        console.error(`Error fetching content for ${pageId}:`, err);
        setError(err.message);
        // Fall back to default content on error
        setContent(defaultContentRef.current);
        setTitle(defaultTitleRef.current);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();

    // Only depend on pageId
  }, [pageId]);

  return { content, title, loading, error };
};

export default useFirebaseContent;
